/**
 * Privacy Policy — Micropincher
 * Update version and lastUpdated on every content change.
 * This file is versioned in git — each change is traceable.
 */
const privacy = {
  version: '1.0',
  lastUpdated: '2026-02-23',
  title: 'Privacy Policy',
  sections: [
    {
      title: '1. Introduction',
      paragraphs: [
        'This Privacy Policy explains how Micropincher ("we", "us", "our") collects, uses, and protects your personal data when you use our platform.',
        'We are committed to protecting your privacy and complying with applicable data protection laws, including the EU General Data Protection Regulation (GDPR).',
      ],
    },
    {
      title: '2. Data We Collect',
      paragraphs: [
        'We collect the following categories of personal data:',
      ],
      list: [
        'Account data: your name, email address, and profile picture — provided via Google Sign-In',
        'Usage data: pages visited, features used, and interactions — collected via Google Analytics',
        'Payment data: billing information and subscription status — processed and stored by Stripe (we do not store card details)',
        'API keys: exchange API keys you voluntarily provide — stored encrypted',
        'Log data: IP addresses and request logs for security and debugging purposes',
      ],
    },
    {
      title: '3. How We Use Your Data',
      paragraphs: [
        'We use your data to:',
      ],
      list: [
        'Provide and operate the Service',
        'Process subscription payments via Stripe',
        'Send service-related notifications (account, billing, updates)',
        'Improve the platform based on aggregated usage analytics',
        'Detect and prevent fraud or abuse',
        'Comply with legal obligations',
      ],
      paragraphsAfterList: [
        'We do not sell your personal data to third parties. We do not use your data for advertising.',
      ],
    },
    {
      title: '4. Third-Party Services',
      paragraphs: [
        'We use the following third-party services that may process your data:',
      ],
      list: [
        'Google OAuth — for authentication. Subject to Google\'s Privacy Policy.',
        'Google Analytics 4 — for usage analytics. Data is anonymised where possible. Subject to Google\'s Privacy Policy.',
        'Stripe — for payment processing. Stripe stores and processes payment data under their own Privacy Policy and PCI-DSS compliance.',
      ],
      paragraphsAfterList: [
        'Each third party is responsible for their own data handling in accordance with their respective privacy policies.',
      ],
    },
    {
      title: '5. Data Retention',
      paragraphs: [
        'We retain your account data for as long as your account is active. If you request account deletion, your personal data will be removed within 30 days, except where retention is required by law.',
        'Payment records are retained for 7 years for accounting and legal compliance.',
        'Server logs are retained for up to 90 days.',
      ],
    },
    {
      title: '6. Your Rights (GDPR)',
      paragraphs: [
        'If you are located in the European Economic Area, you have the following rights:',
      ],
      list: [
        'Right of access — request a copy of the data we hold about you',
        'Right to rectification — request correction of inaccurate data',
        'Right to erasure — request deletion of your personal data',
        'Right to restriction — request that we limit processing of your data',
        'Right to data portability — request your data in a machine-readable format',
        'Right to object — object to processing based on legitimate interests',
      ],
      paragraphsAfterList: [
        'To exercise any of these rights, contact us at privacy@micropincher.com. We will respond within 30 days.',
      ],
    },
    {
      title: '7. Cookies',
      paragraphs: [
        'Micropincher uses the following types of cookies:',
      ],
      list: [
        'Essential cookies — required for the application to function (authentication session)',
        'Analytics cookies — Google Analytics, used to understand how users interact with the platform',
      ],
      paragraphsAfterList: [
        'You can disable analytics cookies in your browser settings. Disabling essential cookies may prevent the application from working correctly.',
      ],
    },
    {
      title: '8. Data Security',
      paragraphs: [
        'We implement appropriate technical and organisational measures to protect your data, including:',
      ],
      list: [
        'Encryption of API keys at rest (AES-256)',
        'HTTPS/TLS for all data in transit',
        'Access controls and authentication for all systems',
      ],
      paragraphsAfterList: [
        'No system is 100% secure. If you discover a security vulnerability, please report it responsibly to security@micropincher.com.',
      ],
    },
    {
      title: '9. Changes to This Policy',
      paragraphs: [
        'We may update this Privacy Policy. Material changes will be communicated via email or a notice in the application.',
        `Current version: 1.0 — Last updated: 2026-02-23`,
      ],
    },
    {
      title: '10. Contact',
      paragraphs: [
        'For privacy-related questions or requests: privacy@micropincher.com',
        'For general enquiries: info@micropincher.com',
      ],
    },
  ],
};

export default privacy;
